/**
 * @module Functional
 */

/* eslint no-shadow: ["error", { "allow": ["path"] }] */

import * as R from 'ramda';

/**
 * @sig headAndTail :: [a] -> [a, [a]]
 */
const headAndTail = a => [R.head(a), R.tail(a)];

/**
 * Add an index to the default R.map method
 * @func mapWithIndex
 * @sig mapWithIndex :: ((a -> b), [a]) -> [b]) -> (((a, Number) -> b), [a]) -> [b])
 */
const mapWithIndex = R.addIndex(R.map);

/**
 * add an index to the default R.reject method
 * @func rejectWithIndex
 * @sig rejectWithIndex :: ((a, Number) -> Boolean), [a]) -> [a]
 */
const rejectWithIndex = R.addIndex(R.reject);

/**
 * Return the indices of every element of array for which f returned something "truthy"
 * @sig Filterable f => (array → Boolean) → f array → [Number]
 */
const filterReturningIndices = (f, array) => {
    const result = [];

    for (let i = 0; i < array.length; i++) {
        if (f(array[i])) result.push(i);
    }

    return result;
};

/**
 * Same as R.path, except:
 *
 * - it's not curried
 * - any key which is a negative number assumes that the object at that point in the path is an array to
 *   be referenced from the *end*
 *
 * @example path(['features',  1], { features: ['a', 'b', 'c'] } => 'b'
 * @example path(['features', -1], { features: ['a', 'b', 'c'] } => 'c'
 * @example path(['features', -2], { features: ['a', 'b', 'c'] } => 'b'
 * @example path(['missing',  -2], { features: ['a', 'b', 'c'] } => undefined
 *
 * @sig path :: ([String], Object) -> *
 */
const path = (keys, o) => {
    for (let i = 0; i < keys.length; i++) {
        let key = keys[i];
        if (typeof key === 'number' && key < 0) key = o.length + key;
        o = o[key];
        if (!o) return undefined;
    }

    return o;
};

/*
 * R.path, but with the path specified as a '.'-separated string for legibility
 *
 * These are all the same (assuming a.b.c exists)
 *
 *   c = a.b.c
 *   path(['a', 'b'], c)
 *   pathString('a.b', c)
 */
const pathString = R.curry((path, o) => R.path(path.split('.'), o));

const pathStringOr = R.curry((defaultValue, path, o) => R.path(path.split('.'), o) || defaultValue);

const pathStringEq = R.curry((path, val, o) => R.pathEq(val, path.split('.'), o));

/**
 * Concat an array of arrays into a single output
 * @sig concatAll :: [[*]] -> [*]
 */
const concatAll = as => R.reduce((acc, a) => acc.concat(a), [], as);

/**
 * Given function f, return function g that will call f after a delay (in ms)--but reset the timer each time g is called
 * @sig debounce :: (Function, Number) -> Function
 */
function debounce(f, delay) {
    let timeout;

    return function toBeCalledLater(...args) {
        const later = () => {
            timeout = null;
            f.apply(_this, args);
        };

        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const _this = this;
        clearTimeout(timeout);
        timeout = setTimeout(later, delay);
    };
}

/**
 * Variation of R.merge that omits any key whose value is undefined
 * @sig mergeOmittingUndefined :: (Object, Object) -> Object
 */
const mergeOmittingUndefined = (o1, o2) => R.pickBy(v => !Object.is(v, undefined), R.mergeRight(o1, o2));

/**
 * Update specific indices of an array by calling a function f on each of them
 * @sig updateMany :: ([Number], a -> a, [a]) -> [a]
 */
const updateMany = (f, indices, a) => R.reduce((acc, index) => R.adjust(index, f, acc), a, indices);

/**
 * Update the specific elements of an array that pass a filter function by calling function f on each of them
 * @sig updateMatching :: ((a -> a), (a -> b), list) => {
 */
const updateMatching = (filter, f, list) => updateMany(f, filterReturningIndices(filter, list), list);

/**
 * Extend the idea of "plucking" a value from an object so it uses a path rather than a simple property
 * @sig pluckPath :: ([String], o) -> [*]
 */
const pluckPath = (path, o) => R.map(R.path(path), o);

/**
 * Create an object from an array of objects, by plucking each element's 'field'
 * @sig String -> [Object] -> Object
 *
 * @example
 *
 *    arrayToLookupTable('id')([   ->  {
 *        { id: 1, a: 4},     ->      1: { id: 1, a: 4},
 *        { id: 2, a: 5 }     ->      2: { id: 2, a: 5 }
 *    ])                      ->  }
 */
const arrayToLookupTable = field => o => R.zipObj(R.pluck(field, o), o);

/**
 * Create an object from an array of objects, by plucking each element's 'field'
 * @sig ([String], [Object]) -> Object
 *
 * @example
 *
 *    arrayToLookupTable(['properties', 'id'], [{
 *        { properties: { id: 1, a: 4 }},    ->      1: { properties: { id: 1, a: 4 }},
 *        { properties: { id: 2, a: 5 }}     ->      2: { properties: { id: 2, a: 5 }}
 *    ])                                     ->  }
 */
const arrayToLookupTableUsingPath = (path, o) => R.zipObj(R.map(R.path(path), o), o);

// :: (Number, Number, Number) -> Boolean
const withinTolerance = (n1, n2, tolerance) => Math.abs(n1 - n2) <= tolerance;

/*
 * Do the objects have the same keys?
 * _objectsHaveSameKeys :: (Object, Object) -> Boolean
 */
const _objectsHaveSameKeys = (o1, o2) => R.equals(R.keys(o1).sort(), R.keys(o2).sort());

/*
 * Are the values of two objects equal at the given key, where "equal" is relaxed to be within a tolerance for numbers?
 * @sig _nearlyEqualAtKey :: (Object, Object, Number) -> String -> Boolean
 */
const _nearlyEqualAtKey = (o1, o2, tolerance) => key => {
    const [v1, v2] = [o1[key], o2[key]];

    switch (typeof v1) {
        case 'number':
            return withinTolerance(v1, v2, tolerance);
        case 'object':
            return nearlyEqualObjects(v1, v2, tolerance);
        default:
            return R.equals(v1, v2);
    }
};

/**
 * Are two objects (including arrays) equal, where 'equals' is relaxed to allow a tolerance?
 * @sig nearlyEqualObjects :: (Object, Object, Number) -> Boolean
 * @param o1 {Object} - object 1
 * @param o2
 * @param tolerance
 * @returns {boolean}
 *
 */
const nearlyEqualObjects = (o1, o2, tolerance) => {
    if (!_objectsHaveSameKeys(o1, o2)) return false;
    const results = R.chain(_nearlyEqualAtKey(o1, o2, tolerance), R.keys(o1));
    return R.all(b => b, results);
};

/**
 * Split an array into two arrays; the contents of the first result will include elements 0, 2... of the original
 * array and the second result will include elements 1, 3... of the original array
 * @sig splitAlternating :: [*] -> [[*], [*]]
 */
const splitAlternating = a => {
    const l = [];
    const r = [];

    for (let i = 0; i < a.length; i++) {
        if (i % 2 === 0) l.push(a[i]);
        else r.push(a[i]);
    }

    return [l, r];
};

/**
 * return the max of the array
 * @sig max :: [*] -> *
 */
const max = a => {
    let m = Number.NEGATIVE_INFINITY;

    for (let i = 0; i < a.length; i++) if (a[i] > m) m = a[i];

    return m;
};

/**
 * return the min of the array
 * @sig max :: [*] -> *
 */
const min = a => {
    let m = Number.POSITIVE_INFINITY;

    for (let i = 0; i < a.length; i++) if (a[i] < m) m = a[i];

    return m;
};

/**
 * Alphabetize an array of strings, *ignoring* alphabetization for any string that appears in the array of exceptions
 * This is useful because the AI Plugin requires that the key 'type' appear first
 * @sig alphabetizeWithExceptions :: [String] -> [String] -> [String]
 */
const alphabetizeWithExceptions = exceptions => {
    const lookup = R.fromPairs(mapWithIndex((s, i) => [s, i - 10000], exceptions));

    return array => {
        const compare = (s1, s2) => {
            const lookup1 = lookup[s1];
            const lookup2 = lookup[s2];

            if (lookup1 && !lookup2) return -1;
            if (lookup2 && !lookup1) return 1;

            const c1 = lookup1 || s1;
            const c2 = lookup2 || s2;

            if (c1 < c2) return -1;
            if (c1 > c2) return 1;
            return 0;
        };

        return array.sort(compare);
    };
};

/**
 * JSON stringify an object, but order keys (nearly) alphabetically and put all "coordinates" on one line
 * alphabetizationExceptions defines exceptions, and defaults to ['type'] because the AI Plugin requires
 * the 'type' to be the first entry in some cases
 * @sig (Object, [String]) -> JsonString
 */
const stringify = (o, alphabetizationExceptions = ['type']) => {
    const isObject = x => typeof x === 'object';
    const alphabetizer = alphabetizeWithExceptions(alphabetizationExceptions);

    const replacer = (key, value) => {
        const sortKeys = () => {
            const o2 = {};
            const sorted = alphabetizer(Object.keys(value));

            for (const k of sorted) o2[k] = value[k];

            return o2;
        };

        if (key === 'coordinates') return JSON.stringify(value);
        if (isObject(value) && !Array.isArray(value) && value) return sortKeys();

        return value;
    };

    const s = JSON.stringify(o, replacer, 2);
    return s.replace(/"coordinates":\s*"(.*)"/g, '"coordinates": $1');
};

/**
 * Does o have all the requiredKeys, zero or more of the optional keys and no other keys?
 * @sig hasExpectedKeys :: ([String], [String], o) -> true|{ missingRequiredKeys:, unexpectedKeys }
 * @return true or a object with missingRequiredKeys, unexpectedKeys
 */
const hasExpectedKeys = (requiredKeys, optionalKeys, o) => {
    const keys = R.keys(o);

    const missingRequiredKeys = R.difference(requiredKeys, keys);
    const unexpectedKeys = R.difference(keys, R.concat(requiredKeys, optionalKeys));

    return missingRequiredKeys.length + unexpectedKeys.length ? { missingRequiredKeys, unexpectedKeys } : true;
};

/**
 * Remove all the values in target for which a key/value pair matches one from the canonical object
 * @sig (Object, Object) -> Object
 */
const removeCanonicalProperties = (canonical, target) => {
    const fn = (acc, key) => (target[key] === canonical[key] ? acc : R.assoc(key, target[key], acc));

    return R.reduce(fn, {}, R.keys(target));
};

/**
 * Replace the key in an object with a new name
 * @sig renameKey :: (String, String, Object) -> Object
 */
const renameKey = (from, to, o) => {
    const value = R.prop(from, o);
    o = R.omit([from], o);
    o = R.assoc(to, value, o);

    return o;
};

/**
 * Rename each key in an object by passing the key to a passed-in function
 * @sig renameKeys :: ((String -> String), Object) -> Object
 */
const renameKeys = (renameFunc, o) =>
    R.reduce(
        (acc, key) => {
            acc[renameFunc(key)] = o[key];
            return acc;
        },
        {},
        R.keys(o),
    );

/**
 * Add 'toAdd' to the object at o[prop]
 * @sig mergeWithProp :: (String, Object, Object) -> Object
 * @example mergeWithProp('f', { a: 3, d: 4 }, { f: { a: 1, b: 2 }})) -> { f: { a: 3, b: 2, d: 4 }}
 */
const mergeWithProp = (prop, toAdd, o) => R.over(R.lensProp(prop), old => R.mergeRight(old || {}, toAdd), o);

/**
 * Add 'toAdd' to the array at o[prop]
 * @sig concatAtProp :: (String, [a], Object) -> Object
 * @example concatAtProp('a', [3, 4], { a: [1, 2] })) -> { a: [1, 2, 3, 4] }
 */
const concatAtProp = (prop, toAdd, o) => R.over(R.lensProp(prop), old => R.concat(old || [], toAdd), o);

/**
 * Add 'toAdd' to the array at o[prop]
 * @sig concatAtProp :: (String, [a], Object) -> Object
 * @example concatAtPath(['a', 'b'], [3, 4], { a: { b: [1, 2] }})) -> { a: { b: [1, 2, 3, 4] }}
 */
const concatAtPath = (path, toAdd, o) => R.over(R.lensPath(path), old => R.concat(old || [], toAdd), o);

/**
 * Omit an element of an object nested at the given path
 * @sig :: ([String], Object) -> Object
 * @example omitAtPath(['a', b'], { a: { b: 1, c: 2 }} -> { a: { c: 2 }}
 */
const omitAtPath = (path, o) => {
    const toOmit = R.last(path);
    const prefixPath = R.dropLast(1, path);
    return R.over(R.lensPath(prefixPath), R.omit([toOmit]), o);
};

/**
 * Remove toRemove from the array found at the path in o
 * @sig :: ([String], [a], Object) -> Object
 * @example withoutAtPath(['a', b'], [1, 3], { a: { b: [1, 2, 3] }} -> { a: { b: [2] }}
 */
const withoutAtPath = (path, toRemove, o) => R.over(R.lensPath(path), R.without(toRemove), o);

/**
 * Group an array of objects using the specified property to group on (variation of R.groupBy)
 * @sig (String, [Object]) -> { k: [Object] }
 * @example
 *  groupByProp('a',
 *      [
 *          { a: 1, b: 1},
 *          { a: 1, b: 2 },
 *          { a: 2, b: 3 }
 *      ])
 *
 *  -> {
 *          1: [{ a: 1, b: 1}, { a: 1, b: 2 }],
 *          2: [{ a: 2, b: 3 }],
 *     }
 */
const groupByProp = (prop, array) => R.groupBy(o => o[prop], array);

/*
 * R.assocPath with the special case that the original object is return if the value is R.nil
 * @sig assocPathUnlessNil ::  ([String], a, Object) -> Object
 */
const assocPathUnlessNil = (path, value, o) => (R.isNil(value) ? o : R.assocPath(path, value, o));

/*
 * Does o have a property at the given path?
 * @sig hasPath :: ([String], Object) -> Boolean
 */
const hasPath = (path, o) => {
    const leafObject = R.path(R.dropLast(1, path), o);

    if (!leafObject) return false;

    const leafProperty = R.last(path);
    return R.has(leafProperty, leafObject);
};

/*
 * Replace an existing element of the array with element, identifying the element to replace using pathToId
 * as the identifier:
 *
 *  const original = [{ id: 1, b: 1 }, { id: 2, b: 2  }, { id: 3, b: 3 }]
 *  const updates  =                   { id: 2, b: 20 }
 *
 *  Functional.replaceElementWithSameId(['id'], updates, original) =>
 *                   [{ id:1,  b: 1 }, { id: 2, b: 20 }, { id: 3, b: 3 }]
 * @sig replaceElementWithSameId :: ([String], Object, [Object]) -> [Object]
 */
const replaceElementWithSameId = (pathToId, element, array) => {
    const elementId = R.path(pathToId, element);
    const matches = array.filter(e => R.path(pathToId, e) === elementId);
    return matches.length ? R.update(R.indexOf(matches[0], array), element, array) : undefined;
};

/*
 * Shorthand for R.over with a prop
 * @sig overProp :: (String, (a -> b), Object) -> Object
 */
const overProp = (prop, func, o) => R.over(R.lensProp(prop), func, o);

/*
 * Shorthand for R.over with a path
 * @sig overPath :: ([String], (a -> b), Object) -> Object
 */
const overPath = (path, func, o) => R.over(R.lensPath(path), func, o);

/**
 * Performs left-to-right function composition. The leftmost function may have
 * any arity; the remaining functions must be unary.
 *
 * In some libraries this function is named `sequence`.
 *
 * **Note:** The result of pipe is not automatically curried.
 *
 * @sig (((a, b, ..., n) -> o), (o -> p), ..., (x -> y), (y -> z)) -> ((a, b, ..., n) -> z)
 * @symb pipe(f, g, h)(a, b) = h(g(f(a, b)))
 * @param {...Function} functions
 * @return {Function}
 * @example
 *
 *      conxt f = R.pipe(Math.pow, R.negate, R.inc);
 *      f(3, 4); // -(3^4) + 1
 *
 * Notes:
 *
 * This version differs from the Ramda version:
 *
 * - no currying at all
 * - this version simply iterates over each function in turn (using a *for loop*!);
 *      the Ramda version is defined recursively so that each successive function is pushed down into
 *      a single function using closures that works as if you had literally called h(g(f(a,b)))l
 *
 * Debugging:
 *
 * - set (global|window)._tracePipe to true to trace the result
 * - OR add a breakpoint at one of the DEBUG HERE points below
 * - OR add a breakpoint at the point where the function *returned* by pipe is *invoked* (not where it's defined)
 *
 *      const times4 = pipe(mul2, mul2)    <-- NO breakpoint here
 *      const answer = times4(5)           <-- breakpoint here instead and then step into pipe
 *
 */
const pipe = (...functions) => {
    // if (arguments.length === 0) throw new Error('pipe requires at least one argument')

    return function _piped(...args) {
        // eslint-disable-next-line no-undef
        const trace = typeof _tracePipe === 'boolean' && _tracePipe;

        // first function can take *any* number of parameters and therefore needs 'apply'
        let nextFunctionInPipe = functions[0];

        // DEBUG HERE...: nextFunctionInPipe.name will (usually) be the name of the first function in the pipe
        let result = nextFunctionInPipe(...args);

        // eslint-disable-next-line no-console
        if (trace) console.log(`pipe: ${nextFunctionInPipe.name}(${[args]}) -> ${result}`);

        // iterate over the functions using the result of one as the input to the next
        for (let i = 1; i < functions.length; i++) {
            nextFunctionInPipe = functions[i];
            const arg = result;

            // ... OR DEBUG HERE: nextFunctionInPipe.name will (usually) be the name of the function about to be called
            result = nextFunctionInPipe(result);

            // eslint-disable-next-line no-console
            if (trace) console.log(`pipe: ${nextFunctionInPipe.name}(${arg}) -> ${result}`);
        }

        return result;
    };
};

/**
 * Performs left-to-right composition of one or more Promise-returning functions.
 * The leftmost function may have any arity; the remaining functions must be unary.
 *
 * @func
 * @category Function
 * @sig ((a -> Promise b), (b -> Promise c), ..., (y -> Promise z)) -> (a -> Promise z)
 * @return {Function}
 * @example
 *
 *      const f = Functional.pipeP(addP, add1P, add2P)
 *      await f(2, 3) // -> 8
 */
const pipeP = (...functions) => {
    // if (arguments.length === 0) throw new Error('pipe requires at least one argument')

    // eslint-disable-next-line no-undef
    const shouldTrace = typeof _tracePipeP === 'boolean' && _tracePipeP;

    // :: (Function, [*], *) -> () (impure)
    const possiblyTrace = (func, args, result) => {
        if (shouldTrace) {
            const functionNames = R.pluck('name', functions);
            // eslint-disable-next-line no-console
            console.log(`trace pipeP(${functionNames}): await ${func.name}(${[args]}) -> ${result}`);
        }
    };

    return async function _piped(...args) {
        let arg;
        let nextFunctionInPipe;

        try {
            // first function can take *any* number of parameters and therefore needs 'apply'
            nextFunctionInPipe = functions[0];

            // ---------------------------------------------------------------------------------------------------------
            //
            // DEBUG HERE: nextFunctionInPipe.name will (usually) be the name of the FIRST function in the pipe
            //
            // ---------------------------------------------------------------------------------------------------------
            let result = await nextFunctionInPipe(...args);
            possiblyTrace(nextFunctionInPipe, args, result);

            // iterate over the remaining functions using the result of one as the input to the next
            for (let i = 1; i < functions.length; i++) {
                nextFunctionInPipe = functions[i];
                arg = result; // track for tracing only

                // ---------------------------------------------------------------------------------------------------------
                //
                // DEBUG HERE: nextFunctionInPipe.name will (usually) be the name of the NEXT function to be called
                //
                // ---------------------------------------------------------------------------------------------------------
                result = await nextFunctionInPipe(result);
                possiblyTrace(nextFunctionInPipe, arg, result);
            }

            return result;
        } catch (e) {
            // rethrow after possibly tracing
            possiblyTrace(nextFunctionInPipe, arg, e);
            throw e;
        }
    };
};

/*
 * Similar to R.assoc, but the second parameter is a function not a value: a property of o is set by calling f(o)
 * @sig assocUsingFunc :: String -> Function -> Object -> Object
 */
const assocUsingFunc = R.curry((prop, func, o) => R.assoc(prop, func(o), o));

/**
 * Memoize function "fn" keeping exactly one previous value. If a subsequent call uses the same parameters,
 * the previous values is returned, otherwise fn is called and its params and result are stored for next time
 */
function memoizeOnceWith(paramsToStringFunc, fn) {
    let lastStringifiedParams;
    let lastValue;

    return function returned(...args) {
        const stringifiedParams = paramsToStringFunc.apply(this, args);
        if (stringifiedParams !== lastStringifiedParams) {
            lastStringifiedParams = stringifiedParams;
            lastValue = fn.apply(this, args);
        }

        return lastValue;
    };
}

/*
 * Memoize function "fn" keeping exactly one previous value. "memoizeIntoObject" will be called on subsequent
 * calls to the memoized function, and if it returns an object that is not "the same" as last time, fn will be
 * called and its value stored and returned; otherwise the last value fn computed will be returned.
 *
 * "The same" here means that for each key in the objects, the old value and the new value must be identical --
 * though the top-level objects themselves need not be identical.
 *
 * @example
 *
 *    const toObject      = o => ({ a: o.a, b: o.b })
 * *  const addAB         = o => o.a + o.b
 *    const memoizedAddAB = F.memoizeOnceWithObject(toObject, addAB)
 *
 *    memoizedAddAB({ a: 5, b: 6 })  -> 11; addAB is called and 11 cached
 *    memoizedAddAB({ a: 5, b: 6 })  -> 11; addAB is not called because toObject returned "the same" object
 *
 */
function memoizeOnceWithObject(memoizeIntoObject, fn) {
    let lastMemoizedObject;
    let lastValue;

    const equalKeys = (a, b) => R.equals(R.keys(a), R.keys(b));

    const equalValues = (a, b) => {
        const valuePairs = R.zip(R.values(a), R.values(b));
        return R.all(([l, r]) => l === r, valuePairs);
    };

    const objectsMatchShallow = (a, b) => equalKeys(a, b) && equalValues(a, b);

    return function _memoizeOnceWithObject(...args) {
        const memoizedObject = memoizeIntoObject.apply(this, args);
        if (!objectsMatchShallow(lastMemoizedObject, memoizedObject)) {
            lastMemoizedObject = memoizedObject;
            lastValue = fn.apply(this, args);
        }

        return lastValue;
    };
}

/*
 * Is the value at the given path from the root o equal to value?
 * @sig ([String], *, Object) -> Boolean
 * @example:
 *
 *      Functional.pathIs(['a', 'b'], 1, { a: { b: 1 } }) -> true
 *      Functional.pathIs(['a', 'b'], 1, { c: { d: 1 } }) -> false
 */
const pathIs = R.curry((path, value, o) => R.path(path, o) === value);

// :: ([String], Object, Object) -> Object
const mergeAtPath = R.curry((path, newValue, o) => R.over(R.lensPath(path), old => R.mergeRight(old, newValue), o));

/*
 * Remove any element of an array that matches the predicateFunction
 * @sig withoutUsing :: ((a -> Boolean), [a]) -> [a]
 */
const withoutUsing = R.curry((predicateFunction, a) => {
    const result = [];

    for (let i = 0; i < a.length; i++) {
        if (!predicateFunction(a[i])) result.push(a[i]);
    }

    return result;
});

/*
 * Throw if there's nil at the given path
 * @sig ([String], a) -> a|Error
 */
const pathOrThrow = (path, o) => {
    const x = R.path(path, o);

    if (R.isNil(x)) throw new Error(`Nothing at ${path}`);
    else return x;
};

const capitalizeFirst = s => `${R.toUpper(R.head(s))}${R.tail(s)}`;

/*
 * Similar to R.cond, except that rather than expecting tuples of [predicate, result] there is just a single func,
 * and if the result of calling f(args) is notNil, that value is returned
 * @sig :: [(*... -> Boolean)] -> (*... -> *)
 *
 * @example:
 *
 *     const divideBy2Or3 = Functional.condNotNil(
 *         x => x % 2 === 0 ? x / 2 : undefined,
 *         x => x % 3 === 0 ? x / 3 : undefined,
 *     )
 *
 *     divideBy2Or3(12) -> 6
 *     divideBy2Or3(15) -> 5
 *     divideBy2Or3(17) -> undefined
 *
 */
const condNotNil =
    (...funcs) =>
    (...args) => {
        for (let i = 0; i < funcs.length; i++) {
            const result = funcs[i].apply(undefined, args);
            if (result) return result;
        }
    };

// ---------------------------------------------------------------------------------------------------------------------
//
// Ramda uncurried
//
// ---------------------------------------------------------------------------------------------------------------------

const is = (constructor, val) => (val != null && val.constructor === constructor) || val instanceof constructor;
const isNil = x => x == null;
const flatten = list => {
    const flatt = list2 => {
        const result = [];

        for (let i = 0; i < list2.length; i++) {
            if (Array.isArray(list2[i])) {
                const value = flatt(list2[i]);

                for (let j = 0; j < value.length; j++) {
                    result[result.length] = value[j];
                }
            } else {
                result[result.length] = list2[i];
            }
        }

        return result;
    };

    return flatt(list);
};

const map = (fn, arrayOrObject) => {
    if (Array.isArray(arrayOrObject)) return arrayOrObject.map(fn);

    const keys = Object.keys(arrayOrObject);
    return keys.reduce((acc, key) => {
        acc[key] = fn(arrayOrObject[key]);
        return acc;
    }, {});
};

const chain = (fn, list) => flatten(map(fn, list));

/*
 * Return true iff o matches the given 'structure'
 *
 * 'structure should be an object where each entry has:
 *
 * - a key that must exist in o
 * - a value which is one of:
 *
 *   - a constructor function (eg. String, Array, etc.) such that R.is(v, o[key]) will return true
 *   - an object literal that defines a recursive structure that will applied recursively to o[key]
 *   - a non-object literal v that must match: v === o[k]
 *
 * If exact is true, no extra elements can exist in structure or o
 *
 * @example:
 *
 *     const structure1 = { a: String, b: { c: Number, d: Object } } // matching c against any number
 *     const structure2 = { a: String, b: { c: 7,      d: Object } } // matching c against the literal 7
 *
 *     const test1      = { a: 'a',    b: { c: 1,      d: { e: 5 } } }
 *     const test2      = { a: 1,      b: { c: 1,      d: { e: 5 } } }
 *     const test3      = { a: 'a',    b: { c: 7,      d: { e: 5 } } }
 *
 *     matchStructure(structure1, test1)       -> true
 *     matchStructure(structure1, test1, true) -> true (no extra fields in test1 not defined in structure1)
 *     matchStructure(structure1, test2)       -> false (o.a is not a String)
 *     matchStructure(structure2, test2)       -> false (o.c doesn't match 7)
 *     matchStructure(structure2, test3)       -> true (o.c matches 7)
 */
const matchStructure = (structure, o, exact = false) => {
    // :: ([String, String|Object]) -> Boolean
    const matchOne = ([k, rhs]) => {
        const recurse = typeof rhs === 'object';
        const literal = rhs !== null && typeof rhs !== 'function';

        if (!R.has(k, o)) return false; // no o[k] at all: obviously doesn't match
        if (recurse) return matchStructure(rhs, o[k], exact); // need to recurse
        if (literal) return o[k] === rhs; // compare the expected literal against the actual
        return is(rhs, o[k]); // does o[k] have the right rhs?
    };

    if (exact && Object.keys(structure).length !== Object.keys(o).length) return false;

    return Object.entries(structure).every(matchOne);
};

/*
 * Wrapper for matchStructure to remove boilerplate
 */
const matchStructures = (structureProcessPairs, errorMessage) => o => {
    for (let i = 0; i < structureProcessPairs.length; i++) {
        const [structure, processor] = structureProcessPairs[i];
        if (matchStructure(structure, o)) return processor;
    }

    // eslint-disable-next-line no-console
    console.warn(errorMessage(o));

    return { error: true, feature: o, message: errorMessage(o) };
};

/*
 * Wrap 'f', returning a function that will console.warn if f takes longer than 'threshold' milliseconds
 * @sig :: ((...* -> *), Number) -> ...* -> *
 */
const warnIfSlow =
    (f, threshold) =>
    (...args) => {
        const start = Date.now();
        const result = f.apply(this, args);
        const duration = Date.now() - start;

        // eslint-disable-next-line no-console
        if (duration > threshold) console.warn('S L O W ', `${f.name}(${args.map(a => a.toString())})`, duration);

        return result;
    };

/*
 * Throw if b is truthy
 */
const throwIf = (b, errorMessage) => {
    if (b) throw new Error(errorMessage);
    return b;
};

/*
 * Throw if b is not truthy
 */
const throwUnless = (b, errorMessage) => {
    if (!b) throw new Error(errorMessage);
    return b;
};

// used in removeNilProperties
const _isObjectNotArray = o => !Array.isArray(o) && is(Object, o);
const _isntNil = o => !isNil(o);

/*
 * Remove any nil properties from o
 */
const removeNilProperties = o => {
    const result = {};

    Object.entries(o).forEach(([k, v]) => {
        v = _isObjectNotArray(v) ? removeNilProperties(v) : v;
        if (_isntNil(v)) result[k] = v;
    });

    return result;
};

/*
 * Remove all nils from an array
 * @sig removeNils :: [*|nil] -> [*]
 */
const removeNils = R.reject(R.isNil);

/*
 * R.map, but then reject all the nils in the result
 * @sig mapRemovingNils = R.pipe(R.map, removeNils)
 *
 */
const mapRemovingNils = R.pipe(R.map, removeNils);

/*
 * Accepts an array of functions (f1, f2, ..., fm) and creates a new function f.
 *
 * f has the same signature as f1 and will call f1 directly, after which it will successively call
 * f2 with the output of f1, then f3 with the output of f2, etc, returning the value returned by fm.
 *
 * However if any fx returns nil, f will stop immediately and return undefined rather than piping to the next function
 *
 * This means that it's safe to include input functions that would fail on a nil input because they'll never be called
 *
 * @example:
 *
 *     const f = pipeWhileNotNil([R.prop('age'), R.inc])
 *
 *     f({age: 1}) // => 2
 *     f({})       // => undefined (because R.prop('age') returned undefined)
 */
const pipeWhileNotNil = R.pipeWith((f, x) => (R.isNil(x) ? x : f(x)));

/*
 * Convert an array of objects into a "text table": an array of strings where each column has been fitted to the
 * longest value for that column.
 *
 * An optional array of column names can be passed in if you want to restrict the keys in the output
 *
 * @example:
 *
 *  textTable([
 *      {'a': 'short', number: 5   },
 *      { a: 'longer', number: 500 }
 *  ])
 *
 * =>
 *      a      | number
 *      short  | 5
 *      longer | 500
 *
 * @sig textTable :: [{k: v}, [String]) -> [String]
 */
const textTable = (objects, keys = R.uniq(R.chain(R.keys, objects))) => {
    // find the length of the longest string in an array (after coercing each element in the array to a string)
    // :: [*] -> Number
    const lengthOfLongest = strings => R.reduce((acc, v) => Math.max(acc, v.toString().length), 0, strings);

    // pad a value to the given length (after coercing it to a string)
    // :: Number -> * -> String
    const padToLength = length => v => v.toString().padEnd(length);

    // convert the data for a "column" of objects into a properly padded array of strings
    // :: String -> [String]
    const padTextInColumn = key => {
        const valuesForKey = R.map(R.propOr('', key), objects);
        const valuesIncludingHeader = R.prepend(key, valuesForKey);
        const longest = lengthOfLongest(valuesIncludingHeader);
        return R.map(padToLength(longest), valuesIncludingHeader);
    };

    // zip previously-padded text columns into rows
    // :: [[String]] -> [String]
    const compositeColumns = () => {
        const rows = R.transpose(paddedColumns);
        return R.map(R.join(' | '), rows);
    };

    const paddedColumns = R.map(padTextInColumn, keys); // :: [[String]]
    return compositeColumns(paddedColumns); // :: [String]
};

export {
    alphabetizeWithExceptions,
    arrayToLookupTable,
    arrayToLookupTableUsingPath,
    assocUsingFunc,
    assocPathUnlessNil,
    capitalizeFirst,
    concatAll,
    concatAtPath,
    concatAtProp,
    debounce,
    filterReturningIndices,
    chain,
    condNotNil,
    groupByProp,
    hasPath,
    hasExpectedKeys,
    headAndTail,
    map,
    mapWithIndex,
    mapRemovingNils,
    matchStructure,
    matchStructures,
    max,
    memoizeOnceWith,
    memoizeOnceWithObject,
    mergeAtPath,
    mergeWithProp,
    mergeOmittingUndefined,
    min,
    nearlyEqualObjects,
    omitAtPath,
    overProp,
    overPath,
    pathIs,
    path,
    pathString,
    pathStringOr,
    pathStringEq,
    pathOrThrow,
    pipe,
    pipeP,
    pipeWhileNotNil,
    pluckPath,
    rejectWithIndex,
    removeCanonicalProperties,
    removeNilProperties,
    removeNils,
    renameKey,
    renameKeys,
    replaceElementWithSameId,
    splitAlternating,
    stringify,
    textTable,
    throwIf,
    throwUnless,
    updateMany,
    warnIfSlow,
    updateMatching,
    withoutAtPath,
    withoutUsing,
};
